import React, { useMemo, useState } from "react";
import { Bar } from "react-chartjs-2";
import { Chart, registerables } from "chart.js";
import { Alert, Card, DatePicker, Spin, Typography } from "antd";
import dayjs, { Dayjs } from "dayjs";
import useFetchData from "shared/hooks/useFetchData";

import { API_BASE_URL } from "shared/api/config";
import "./Report.css";

Chart.register(...registerables);

const { Title, Text } = Typography;

interface PaymentBreakdown {
  cash?: number;
  card?: number;
  account?: number;
}

interface Booking {
  day: string;
  year?: string;
  paymenType: string;
  paymentBreakdown?: PaymentBreakdown;
  prePay: number;
  postPay: number;
  addPayment: number;
  packageName: string;
}

interface ApiResponse {
  success: boolean;
  message: string;
  data: Booking[];
}

interface ChartComponentProps {
  theme?: "dark" | "light";
}

const chartColors = [
  "#0f766e",
  "#2563eb",
  "#f59e0b",
  "#dc2626",
  "#7c3aed",
  "#059669",
  "#475569",
  "#db2777",
];

const getMoneyByPaymentType = (booking: Booking) => {
  const split = booking.paymentBreakdown;
  const hasSplit =
    Number(split?.cash || 0) + Number(split?.card || 0) + Number(split?.account || 0) > 0;

  if (hasSplit) {
    return {
      Cash: Number(split?.cash || 0),
      Card: Number(split?.card || 0),
      Account: Number(split?.account || 0),
    };
  }

  return {
    Cash: booking.paymenType === "Cash" ? Number(booking.prePay || 0) + Number(booking.postPay || 0) : 0,
    Card: booking.paymenType === "Card" ? Number(booking.prePay || 0) + Number(booking.postPay || 0) : 0,
    Account:
      booking.paymenType === "Account" ? Number(booking.prePay || 0) + Number(booking.postPay || 0) : 0,
  };
};

const ChartComponent: React.FC<ChartComponentProps> = ({ theme = "light" }) => {
  const [year, setYear] = useState<Dayjs>(dayjs());
  const selectedYear = year.format("YYYY");
  const isDark = theme === "dark";
  const chartTextColor = isDark ? "#eeeeee" : "#334155";
  const chartMutedColor = isDark ? "#b8b8b8" : "#64748b";
  const chartGridColor = isDark ? "rgba(255,255,255,0.12)" : "rgba(15,23,42,0.08)";
  const { data, loading, error } = useFetchData<ApiResponse>(
    `${API_BASE_URL}/book/get?page=1&limit=5000&year=${selectedYear}`
  );

  const {
    labels,
    dailyPaymentData,
    paymentTypeData,
    packageCountData,
    packageAmountData,
  } = useMemo(() => {
    const dailyTotals: Record<string, { prePay: number; postPay: number; addPayment: number }> = {};
    const byPaymentType: Record<string, Record<string, number>> = {};
    const byPackageCount: Record<string, Record<string, number>> = {};
    const byPackageAmount: Record<string, Record<string, number>> = {};

    (data?.data || []).forEach((booking) => {
      if (!booking.day) return;

      const day = booking.day;
      dailyTotals[day] ||= { prePay: 0, postPay: 0, addPayment: 0 };
      dailyTotals[day].prePay += Number(booking.prePay || 0);
      dailyTotals[day].postPay += Number(booking.postPay || 0);
      dailyTotals[day].addPayment += Number(booking.addPayment || 0);

      byPaymentType[day] ||= {};
      Object.entries(getMoneyByPaymentType(booking)).forEach(([type, amount]) => {
        if (amount > 0) {
          byPaymentType[day][type] = (byPaymentType[day][type] || 0) + amount;
        }
      });

      byPackageCount[day] ||= {};
      byPackageAmount[day] ||= {};
      const packageName = booking.packageName || "Багцгүй";
      byPackageCount[day][packageName] = (byPackageCount[day][packageName] || 0) + 1;
      byPackageAmount[day][packageName] =
        (byPackageAmount[day][packageName] || 0) +
        Number(booking.prePay || 0) +
        Number(booking.postPay || 0) +
        Number(booking.addPayment || 0);
    });

    const sortedLabels = Object.keys(dailyTotals).sort((a, b) => a.localeCompare(b));
    const paymentTypes = Array.from(
      new Set(sortedLabels.flatMap((day) => Object.keys(byPaymentType[day] || {})))
    );
    const packageTypes = Array.from(
      new Set(sortedLabels.flatMap((day) => Object.keys(byPackageCount[day] || {})))
    );

    return {
      labels: sortedLabels,
      dailyPaymentData: {
        labels: sortedLabels,
        datasets: [
          {
            label: "Урьдчилгаа",
            data: sortedLabels.map((day) => dailyTotals[day].prePay),
            backgroundColor: "#0f766e",
          },
          {
            label: "Үлдэгдэл",
            data: sortedLabels.map((day) => dailyTotals[day].postPay),
            backgroundColor: "#2563eb",
          },
          {
            label: "Нэмэлт төлбөр",
            data: sortedLabels.map((day) => dailyTotals[day].addPayment),
            backgroundColor: "#f59e0b",
          },
        ],
      },
      paymentTypeData: {
        labels: sortedLabels,
        datasets: paymentTypes.map((type, index) => ({
          label: type,
          data: sortedLabels.map((day) => byPaymentType[day]?.[type] || 0),
          backgroundColor: chartColors[index % chartColors.length],
        })),
      },
      packageCountData: {
        labels: sortedLabels,
        datasets: packageTypes.map((type, index) => ({
          label: type,
          data: sortedLabels.map((day) => byPackageCount[day]?.[type] || 0),
          backgroundColor: chartColors[index % chartColors.length],
        })),
      },
      packageAmountData: {
        labels: sortedLabels,
        datasets: packageTypes.map((type, index) => ({
          label: type,
          data: sortedLabels.map((day) => byPackageAmount[day]?.[type] || 0),
          backgroundColor: chartColors[index % chartColors.length],
        })),
      },
    };
  }, [data]);

  const chartMinWidth = Math.max(760, labels.length * 44);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top" as const,
        align: "start" as const,
        labels: {
          color: chartTextColor,
          boxWidth: 14,
          boxHeight: 14,
          padding: 14,
          font: {
            size: 13,
            weight: "bold" as const,
          },
        },
      },
      tooltip: {
        titleFont: { size: 13, weight: "bold" as const },
        bodyFont: { size: 13 },
        padding: 12,
      },
    },
    scales: {
      x: {
        grid: {
          color: chartGridColor,
        },
        ticks: {
          color: chartMutedColor,
          autoSkip: true,
          maxTicksLimit: 18,
          maxRotation: 0,
          minRotation: 0,
          font: {
            size: 12,
            weight: "bold" as const,
          },
        },
      },
      y: {
        beginAtZero: true,
        grid: {
          color: chartGridColor,
        },
        ticks: {
          color: chartMutedColor,
          font: {
            size: 12,
            weight: "bold" as const,
          },
          callback: (value: string | number) => {
            const numericValue = Number(value);
            if (Number.isNaN(numericValue)) return value;
            if (numericValue >= 1000000) return `${Math.round(numericValue / 1000000)}M`;
            if (numericValue >= 1000) return `${Math.round(numericValue / 1000)}k`;
            return numericValue;
          },
        },
      },
    },
  };

  const renderChart = (chartData: typeof dailyPaymentData) => (
    <div className="report-chart-scroll">
      <div className="report-chart-canvas" style={{ minWidth: chartMinWidth }}>
        <Bar data={chartData} options={options} />
      </div>
    </div>
  );

  if (loading) return <Spin />;
  if (error) return <Alert message="График дата татахад алдаа гарлаа" type="error" showIcon />;

  return (
    <div className="report-page">
      <div className="report-toolbar">
        <div>
          <Title level={3}>График</Title>
          <Text type="secondary">{selectedYear} оны бүртгэл дээр үндэслэв</Text>
        </div>
        <DatePicker picker="year" value={year} onChange={(d) => d && setYear(d)} allowClear={false} />
      </div>

      {labels.length === 0 ? (
        <Alert message="Сонгосон онд график зурах дата алга" type="info" showIcon />
      ) : (
        <div className="report-chart-grid">
          <Card title="Өдөр бүрийн төлбөр" className="report-chart-card">
            {renderChart(dailyPaymentData)}
          </Card>
          <Card title="Төлбөрийн хэлбэр" className="report-chart-card">
            {renderChart(paymentTypeData)}
          </Card>
          <Card title="Багцын тоо" className="report-chart-card">
            {renderChart(packageCountData)}
          </Card>
          <Card title="Багцын дүн" className="report-chart-card">
            {renderChart(packageAmountData)}
          </Card>
        </div>
      )}
    </div>
  );
};

export default ChartComponent;
